// ---------------------------------------------------------------------------
// V17a — THE PLATFORM REVIEW DESK. The one screen in this product that is not about
// an organisation.
//
// It exists because `0035` closed a door on purpose. `verified_by_human` — the
// resolution that turns a provider's refusal into a pass — was made reachable by
// NOBODY, because the only candidate was the agency's own compliance user and an
// agency that can clear its own crew verifies nobody. The consequence was written down
// at the time: **a legitimate worker whose passport a provider misread had no route
// back.** Everything below is that route.
//
// ---------------------------------------------------------------------------
// FOUR THINGS THIS SCREEN IS SHAPED BY
//
//  1. IT IS THE CONSOLE SURFACE, NOT A FIFTH ONE. A new `data-surface` needs its own
//     contrast-tested token set and `verify-design-parity.sh` asserts the four existing
//     sets are complete — a slice of work for one screen. The Control Room dark palette
//     is the right register for an internal operations desk anyway. Run 31 records the
//     decision and the rejected alternative.
//
//  2. IT HOLDS NO VOCABULARY. The tier's own label comes from the server, and the
//     resolutions come from `/v1/reference` rather than a list here — a screen that
//     spelled a resolution id would be a second copy of `verification_review_resolution`
//     and the day they disagreed the copy would be the liar. Same rule as V16's worker
//     app and V8's role screen.
//
//  3. IT SHOWS THE PERSON'S NAME AND NOTHING ELSE ABOUT THEM. No worker id, no cost, no
//     provider reference — the projection in `app.platform_review_queue()` carries none
//     of those, so there is nothing here to leak even by accident. A reviewer is about
//     to make a decision about a human being, and doing that from a uuid is how people
//     get treated as records.
//
//  4. AND IT NEVER SHOWS WHY A CHECK FAILED, because we are never told. §6 is explicit
//     that a provider's reason is not ours to hold, and a screen with a field for it
//     would eventually have one filled in.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_, type PlatformReview } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { isPlatformStaff } from "../session/access";
import { whenIn } from "../system/format";
import { usePartialLoad } from "./PartialLoad";

type Resolution = { id: string; label: string; requires_platform: boolean; is_verified: boolean };

export function PlatformReviews() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const partial = usePartialLoad();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [reviews, setReviews] = useState<PlatformReview[] | null>(null);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { status, body } = await send<{ reviews: PlatformReview[] }>(
      console_.platformReviews(apiBase),
    );
    if (status === 200) setReviews(body?.reviews ?? []);
    else setReviews([]);
  }, [apiBase]);

  useEffect(() => {
    void load();
    // THE RESOLUTIONS ARE FETCHED, NOT LISTED. See note 2 above.
    void send<{ resolutions: Resolution[] }>(console_.verificationResolutions(apiBase)).then(
      ({ status, body }) => {
        if (status === 200) setResolutions(body?.resolutions ?? []);
        else partial.note();
      },
    ).catch(() => partial.note());
  }, [apiBase, load]);

  const settle = useCallback(
    async (id: string, resolution: string) => {
      setBusy(id);
      setFailed(null);
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.resolvePlatformReview(apiBase, id, resolution),
      );
      setBusy(null);
      if (status === 200) {
        await load();
      } else {
        // THE SERVER'S SENTENCE, VERBATIM. Not one composed here from a status code —
        // that would be a second implementation of a rule the database owns.
        setFailed(refusal(body?.error) ?? t("That could not be settled. Try again."));
      }
    },
    [apiBase, load],
  );

  // `me` IS NULL WHILE THE FIRST /v1/me IS IN FLIGHT, and a screen that treated that as
  // "not staff" would flash the refusal panel on every cold load. Null is "not known
  // yet", which is a third state and not a synonym for no.
  if (me === null) {
    return (
      <section className="panel">
        <h1>{t("Identity reviews")}</h1>
        <p>{t("Loading.")}</p>
      </section>
    );
  }

  // NOT STAFF, NOTHING AT ALL. The API refuses them anyway — twice, at
  // `requirePlatform` and again inside `app.platform_review_queue()` — so this is
  // courtesy rather than a control, which is the correct amount of load for a
  // client-side check to bear.
  if (!isPlatformStaff(me)) {
    return (
      <section className="panel">
        <h1>{t("Identity reviews")}</h1>
        <p>{t("This desk is for Verbunk staff. Your access does not include it.")}</p>
      </section>
    );
  }

  return (
    <section className="panel">
      {partial.notice}
      <h1>{t("Identity reviews")}</h1>
      {/* WHAT THIS DESK IS FOR, IN ONE SENTENCE, because a reviewer who does not know
          why they are here will approve things. §6: providers refuse legitimate
          documents often enough that automatic rejection would exclude real workers,
          disproportionately those with unusual or non-EU documents. */}
      <p>
        {t("Each of these people tried to take a shift and a provider could not confirm their document automatically. That happens to genuine documents often enough that it is not a verdict — it is why a person looks.")}
      </p>

      {failed ? <StatusChip tone="failed" label={failed} /> : null}

      {reviews === null ? (
        <p>{t("Loading.")}</p>
      ) : reviews.length === 0 ? (
        // AN EMPTY QUEUE IS A SENTENCE, not a blank panel. Somebody has to be able to
        // tell "nobody is waiting" from "this did not load".
        <p>{t("Nobody is waiting. Every identity check has been settled.")}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Person")}</th>
              <th scope="col">{t("Organisation")}</th>
              <th scope="col">{t("Check")}</th>
              <th scope="col">{t("Waiting since")}</th>
              <th scope="col">{t("Tried")}</th>
              <th scope="col">{t("Decision")}</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((r) => (
              <tr key={r.id}>
                <td>{r.workerName}</td>
                <td>{r.organisation}</td>
                {/* THE TIER'S OWN LABEL, from the server. A tier renamed in the
                    database renames itself here. */}
                <td>{r.tierLabel}</td>
                <td>{whenIn(r.openedAt, orgZone)}</td>
                {/* HOW MANY TIMES THEY TRIED. Somebody who has tapped Accept four times
                    is not the same as somebody who tried once, and a desk that could
                    not see the difference would work the queue in the wrong order. */}
                <td>{r.attempts}</td>
                <td>
                  <div className="worker-actions">
                    {resolutions.map((res) => (
                      <button
                        key={res.id}
                        type="button"
                        // THE VERIFYING RESOLUTION IS NOT THE QUIET ONE. It is the only
                        // button here that grants somebody work, and it should not look
                        // like the others.
                        className={res.is_verified ? "btn" : "btn btn-quiet"}
                        disabled={busy === r.id}
                        onClick={() => void settle(r.id, res.id)}
                      >
                        {translateLabel(locale, res.label)}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
